/**
 * Air dry clay ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_paint_crafts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const airDryClayIdeasPack: ToilePack = {
    slug: 'air-dry-clay-ideas',
    category: 'craft',
    name: 'Air dry clay ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'dish', label: 'Trinket dish', subject: 'a small air dry clay trinket dish painted with a checker pattern'},
        {key: 'earrings', label: 'Earrings', subject: 'air dry clay earrings in pastel arches'},
        {key: 'vase', label: 'Bud vase', subject: 'a small bud vase with a wavy rim'},
    ],
    styles: [
        {key: 'vanity', label: 'Styled at home', style: 'styled at home on a white dresser beside a small mirror, a ring and a sprig of dried flowers', palette: 'Soft whites and creams around the pastel clay colours.', framing: 'Shot at a slight high angle, the piece centred with the styling around it.'},
        {key: 'craft', label: 'Craft table', style: 'on a craft table next to a block of white clay, a rolling pin, a clay knife and small paint pots', palette: 'Pale wood and white clay, the painted piece the one place of colour.', framing: 'Shot from directly above, the finished piece centred with the tools at the margins.'},
        {key: 'listing', label: 'Shop photo', style: 'photographed for a handmade shop listing on a plain warm beige paper sweep', palette: 'One calm beige ground, the clay colours clean and true.', framing: 'Shot at a three-quarter angle, the piece isolated in the centre with generous space around it.'},
    ],
    directions: ['mushroom', 'jewellery tray', 'terracotta'],
    directionHint: 'try: mushroom',
    seo: {
        title: 'Air dry clay ideas: trinket dish, earrings, vase',
        metaDescription: 'Nine air dry clay ideas: a trinket dish, pastel earrings and a bud vase, styled at home, on a craft table and on plain paper.',
        h1: 'Air dry clay ideas: trinket dish, earrings, bud vase',
        tagline: 'Three small clay pieces, each styled at home, set out on a craft table and photographed on plain paper.',
        intro: 'Air dry clay hardens without a kiln, so small pieces can be shaped and painted at a kitchen table. These nine show three ideas: a trinket dish painted with a checker pattern, pastel arch earrings and a bud vase with a wavy rim. Each is styled on a dresser, set out on a craft table beside the clay, or shown alone on a warm beige paper background. Type another piece, such as a mushroom, to ask for it.',
        useCases: [
            'a trinket dish for a dresser',
            'a pair of earrings or a small gift',
            'a bud vase for dried flowers',
            'a handmade item for a small shop listing',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'A trinket dish, pastel earrings and a bud vase, each shown styled at home, on a craft table and on a plain paper background.'},
            {q: 'Are these photos of real clay pieces?', a: 'No. They are generated pictures of each design, not photographs of finished work by a maker.'},
            {q: 'Can I ask for a different piece?', a: 'Yes. Type an item such as a mushroom or a jewellery tray. A typed item is a request, not a promised result.'},
            {q: 'Which view suits a shop listing?', a: 'The shop photo: the piece alone at a three-quarter angle, on a calm warm beige paper background.'},
        ],
    },
}
